import React, { useState, useContext, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Checkbox,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Stack,
  ChakraProvider,
} from "@chakra-ui/react"
import { useToasts } from "react-toast-notifications"
import { useTranslation } from "react-i18next"
import get from "lodash.get"

import { AuthUserContext } from "../../../context/Session"
import { withFirebase } from "../../../utils/Firebase"
import { paymentChargeUrl } from "../../../utils/App"
import businessEmployees from "../../../utils/Constants/Business_Employees"
import toggleMessagingAccess from "../../../utils/activateMessagingAcess"

const MessagingAccessToggle = ({
  firebase,
  handleConfirmOutside,
  onConfirm,
  isCheckedOutsite,
  employeeRangeQuantity,
}) => {
  const authUser = useContext(AuthUserContext)
  const { i18n } = useTranslation()
  const lang = get(i18n, "language", "es").split("-")

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { addToast } = useToasts()

  const [isChecked, setIsChecked] = useState(authUser.hasMessagingAccess)
  const [isLoading, setIsLoading] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [totalAmount, setTotalAmount] = useState(0)
  const [employeeRangeQty, setEmployeeRangeQty] = useState(0)
  const [messagingFee, setMessagingFee] = useState(0)
  const [currentOrg, setCurrentOrg] = useState(null)

  useEffect(() => {
    const fetchPricing = async () => {
      const pricingSnapshot = await firebase.getPricing()
      if (pricingSnapshot.exists) {
        const getData = pricingSnapshot.data()
        setMessagingFee(getData?.businessCharges?.adminMessagingFee)
      }
    }

    fetchPricing()
  }, [firebase])

  useEffect(() => {
    const fetchOrgData = async () => {
      if (!authUser?.organizationId) return

      let orgData = null
      if (authUser?.organizationId) {
        const orgSnap = await firebase.org(authUser.organizationId).get()
        orgData = orgSnap.data()
        setCurrentOrg(orgData)
      }

      const hasMessaging = !!orgData?.hasMessagingAccess
      setIsChecked(hasMessaging)
    }

    fetchOrgData()
  }, [authUser?.organizationId, firebase])

  useEffect(() => {
    let finalEmployeeQty = currentOrg?.employeeRangeQty || employeeRangeQuantity
    if (!finalEmployeeQty) {
      const rangeKey = currentOrg?.employeeRange
      const rangeObj = businessEmployees?.[lang]?.find(
        (r) => r.value === rangeKey
      )

      if (rangeObj) {
        const upperBound = parseInt(rangeObj.label.split("-")[1], 10)
        finalEmployeeQty = upperBound
      }
    }

    if (finalEmployeeQty) {
      setEmployeeRangeQty(finalEmployeeQty)
      setTotalAmount(finalEmployeeQty * messagingFee)
    } else {
      setTotalAmount(0)
    }
  }, [employeeRangeQuantity, currentOrg, lang, firebase, messagingFee])

  const handleConfirm = async () => {
    setIsLoading(true)

    const res = await toggleMessagingAccess({
      organizationId: authUser.organizationId,
      enableMessaging: !isCancelling,
      totalAmount,
      messagingFee,
      employeeRangeQty,
      firebase,
      paymentChargeUrl,
      addToast,
    })

    setIsLoading(false)

    if (res.success) {
      const refreshedUser = await firebase.refreshAuthUser()
      setIsChecked(!!refreshedUser.hasMessagingAccess)
      onClose()
      window.location.reload()
    }
  }

  const handleClose = () => {
    setIsChecked(false)
    onClose()
  }

  const handleSubmit = () => {
    if (handleConfirmOutside) {
      onConfirm(!isCheckedOutsite)
      onClose()
    } else {
      handleConfirm()
    }
  }

  const isCancellingActive = isCancelling || isCheckedOutsite
  const employeeQuantity = employeeRangeQty || employeeRangeQuantity
  return (
    <ChakraProvider>
      <Stack>
        <Checkbox
          isChecked={isChecked || isCheckedOutsite}
          isDisabled={isLoading}
          onChange={() => {
            if (isChecked) {
              setIsCancelling(true)
              onOpen()
            } else {
              onOpen()
            }
          }}
          colorScheme="teal"
        >
          <Text as="span" fontWeight="bold">
            Adquirir Sistema de Comunicación
          </Text>
        </Checkbox>

        <Text
          as="span"
          fontWeight="normal"
          color="gray.400"
          maxWidth="container.md"
        >
          El sistema de comunicación empresarial DOC es una herramienta de
          comunicación directa entre su empresa y sus colaboradores, esta
          funcionalidad representa un monto adicional en su mensualidad.
        </Text>
      </Stack>

      <Modal isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isCancellingActive
              ? "Desactivar funcionalidad de mensajería"
              : "Activar funcionalidad de mensajería"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              {isCancellingActive ? (
                <Text>
                  ¿Estás seguro de que deseas <strong>desactivar</strong> el
                  sistema de mensajería push? Los administradores y
                  comunicadores perderán acceso a esta funcionalidad.
                </Text>
              ) : (
                <>
                  <Text>
                    El sistema de comunicación empresarial DOC es una
                    funcionalidad extra que la organización deberá adicionar a
                    su mensualidad.
                  </Text>
                  <Text fontWeight="semibold">
                    {`Costo por colaborador: $${messagingFee}`}
                  </Text>
                  <Text>
                    Rango de Colaboradores: <b>{employeeQuantity}</b>
                  </Text>
                  <Text fontSize="lg" fontWeight="bold">
                    Total: ${totalAmount.toFixed(2)} USD
                  </Text>
                </>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              colorScheme={isCancellingActive ? "red" : "teal"}
              onClick={handleSubmit}
              isLoading={isLoading}
              isDisabled={messagingFee <= 0 || employeeQuantity <= 1}
            >
              {isCancellingActive
                ? "Confirmar desactivación"
                : "Confirmar y pagar"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </ChakraProvider>
  )
}

MessagingAccessToggle.defaultProps = {
  handleConfirmOutside: false,
  isCheckedOutsite: false,
  onConfirm: () => {},
  employeeRangeQuantity: 0,
}

MessagingAccessToggle.propTypes = {
  firebase: PropTypes.shape().isRequired,
  handleConfirmOutside: PropTypes.bool,
  onConfirm: PropTypes.func,
  isCheckedOutsite: PropTypes.bool,
  employeeRangeQuantity: PropTypes.number,
}

export default withFirebase(MessagingAccessToggle)
